import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/asmrData';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9q2fs7nahb7382ov530', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof msg === 'string' && (msg.toLowerCase().includes('spam') || msg.includes('spam'))) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const brandValues = [
    { icon: 'ri-heart-pulse-line', title: '治愈为本', desc: '一切内容以用户的放松和治愈体验为出发点，精心挑选每一段音频。' },
    { icon: 'ri-shield-star-line', title: '品质至上', desc: '严格把控内容质量，所有音频都经过专业录制和后期处理，确保最佳听感。' },
    { icon: 'ri-lightbulb-flash-line', title: '持续创新', desc: '不断探索新的ASMR形式和触发音类型，为用户带来更多惊喜体验。' },
    { icon: 'ri-user-heart-line', title: '用户为先', desc: '倾听用户反馈，根据大家的需求持续优化内容和平台功能。' },
  ];

  return (
    <div className="pt-24 pb-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: '关于我们' },
        ]} />

        <section className="mt-8 mb-16">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground-900 mb-6">
              关于 asmr1
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed mb-6">
              asmr1 诞生于对声音疗愈力量的坚定信念。我们相信，在快节奏的现代生活中，每个人都值得拥有一片宁静的声音港湾。
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              作为沉浸式ASMR音频体验的首选平台，asmr1 汇聚了来自各地的优质ASMR内容创作者，覆盖触发音合集、助眠放松、减压冥想、角色扮演、吃播口腔音和自然白噪音六大分类。无论你是ASMR新手还是资深爱好者，都能在这里找到最适合自己的声音内容。
            </p>
            <p className="text-foreground-600 leading-relaxed">
              我们的使命是让更多人感受到ASMR带来的深度放松与颅内高潮体验，帮助每一位听众改善睡眠质量、缓解压力、提升生活幸福感。
            </p>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-heading text-3xl font-bold text-foreground-900 mb-10 text-center">品牌价值观</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {brandValues.map((v) => (
              <div key={v.title} className="bg-background-100 rounded-2xl p-6 md:p-7 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600 mx-auto mb-4">
                  <i className={`${v.icon} text-2xl`}></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-heading text-3xl font-bold text-foreground-900 mb-10 text-center">用户口碑</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-background-100 rounded-2xl p-6 md:p-7">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-5">&ldquo;{t.content}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 font-medium text-sm">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-2xl mx-auto">
          <div className="bg-background-100 rounded-2xl p-8 md:p-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-2 text-center">联系我们</h2>
            <p className="text-foreground-600 text-center mb-8 leading-relaxed">
              有任何问题、建议或合作意向？欢迎随时与我们联系
            </p>

            {formStatus === 'success' ? (
              <div className="bg-accent-100/50 text-accent-800 rounded-xl p-5 text-center">
                <i className="ri-checkbox-circle-line text-3xl mb-2 block"></i>
                <p className="font-medium">消息已发送成功！</p>
                <p className="text-sm mt-1">感谢你的来信，我们会尽快回复。</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} data-readdy-form="true" className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors duration-200 text-sm"
                    placeholder="你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors duration-200 text-sm"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors duration-200 text-sm resize-none"
                    placeholder="请输入你的留言内容..."
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3.5 bg-accent-500 text-background-50 rounded-xl text-sm font-medium hover:bg-accent-600 transition-colors duration-200 disabled:opacity-60 whitespace-nowrap"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送消息'}
                </button>
              </form>
            )}
            {formStatus === 'error' && formError && (
              <p className="mt-3 text-sm text-red-500 text-center">{formError}</p>
            )}
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于 asmr1',
              description: 'asmr1 是沉浸式ASMR音频体验的首选平台。了解我们的品牌故事、价值观和联系方式。',
              url: 'https://asmr1.com/about',
              inLanguage: 'zh-Hans',
              isPartOf: { '@type': 'WebSite', name: 'asmr1', url: 'https://asmr1.com' },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr1.com' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            }),
          }}
        />
      </div>
    </div>
  );
}